import React, { useMemo, useState } from "react";
import { ClipboardPaste, Plus, Trash2, Truck, X } from "lucide-react";
import Modal from "../../../components/Modal";
import { DIAS_SEMANA_BASE } from "../../../utils/controleProducaoDates";
import type { ListaFornecedor } from "../../../services/fichaTransferenciaService";
import {
  quantidadeListaFornecedor,
  rotuloTransferenciaLista,
} from "./fichaTransferenciaPdfDados";
import {
  interpretarPedidoWhatsapp,
  listasDoPedido,
  secoesPadraoDestino,
  type ListaPedidoWhatsapp,
} from "./pedidoWhatsapp";

export type ItemListaForm = {
  chave: string;
  quantidade: string;
  unidade: string;
  descricao: string;
};

export type ListaForm = {
  chave: string;
  fornecedor: string;
  previsaoChegada: string;
  /** ISO yyyy-mm-dd; vazio segue a previsão de entrega da ficha. */
  dataTransferencia: string;
  itens: ItemListaForm[];
};

const ID_DIAS_CHEGADA = "ficha-transferencia-dias-chegada";

let sequencia = 0;
function novaChave(): string {
  sequencia += 1;
  return `lista-${sequencia}`;
}

export function novoItemLista(
  dados?: Partial<Omit<ItemListaForm, "chave">>
): ItemListaForm {
  return {
    chave: novaChave(),
    quantidade: dados?.quantidade ?? "",
    unidade: dados?.unidade ?? "",
    descricao: dados?.descricao ?? "",
  };
}

export function novaListaForm(
  dados?: Partial<ListaPedidoWhatsapp & { dataTransferencia: string }>
): ListaForm {
  const itens = (dados?.itens ?? []).map((item) => novoItemLista(item));
  return {
    chave: novaChave(),
    fornecedor: dados?.fornecedor ?? "",
    previsaoChegada: dados?.previsaoChegada ?? "",
    dataTransferencia: dados?.dataTransferencia ?? "",
    itens: itens.length > 0 ? itens : [novoItemLista()],
  };
}

export function listaFormDaApi(lista: ListaFornecedor): ListaForm {
  return novaListaForm({
    fornecedor: lista.fornecedor,
    previsaoChegada: lista.previsaoChegada ?? "",
    dataTransferencia: lista.dataTransferencia ?? "",
    itens: lista.itens.map((item) => ({
      quantidade: item.quantidade ?? "",
      unidade: item.unidade ?? "",
      descricao: item.descricao,
    })),
  });
}

export function listasFormParaPayload(
  listas: ListaForm[]
): Omit<ListaFornecedor, "id" | "ordem">[] {
  return listas
    .map((lista) => ({
      fornecedor: lista.fornecedor.trim(),
      previsaoChegada: lista.previsaoChegada.trim() || null,
      dataTransferencia: lista.dataTransferencia.trim() || null,
      itens: lista.itens
        .filter((item) => item.descricao.trim())
        .map((item) => ({
          quantidade: item.quantidade.trim() || null,
          unidade: item.unidade.trim() || null,
          descricao: item.descricao.trim(),
        })),
    }))
    .filter((lista) => lista.fornecedor || lista.itens.length > 0);
}

export function erroListasForm(
  listas: ListaForm[],
  dataFicha: string
): string | null {
  for (let indice = 0; indice < listas.length; indice += 1) {
    const lista = listas[indice];
    const numero = indice + 1;
    const data = lista.dataTransferencia.trim();
    if (data && data < dataFicha) {
      return `A data de transferência da lista ${numero} não pode ser antes da ficha diária.`;
    }
    const preenchidos = lista.itens.filter((item) => item.descricao.trim());
    if (preenchidos.length > 0 && !lista.fornecedor.trim()) {
      return `Informe o fornecedor da lista ${numero}.`;
    }
    const semProduto = lista.itens.some(
      (item) =>
        !item.descricao.trim() &&
        (item.quantidade.trim() || item.unidade.trim())
    );
    if (semProduto) {
      return `Na lista ${numero} há uma linha com quantidade e sem produto.`;
    }
  }
  return null;
}

const INPUT =
  "block h-10 w-full rounded-lg border border-neutral-300 bg-white px-3 text-sm";

type SecaoProps = {
  listas: ListaForm[];
  somenteLeitura: boolean;
  /** Data da ficha diária (ISO); nenhum bloco transfere antes dela. */
  dataFicha: string;
  /** Rótulo do dia de entrega da ficha, usado quando o bloco não tem data própria. */
  previsaoEntrega: string;
  onChange: (listas: ListaForm[]) => void;
  onImportadas?: (quantidade: number) => void;
};

export function ListasFornecedoresSecao({
  listas,
  somenteLeitura,
  dataFicha,
  previsaoEntrega,
  onChange,
  onImportadas,
}: SecaoProps) {
  const [colarAberto, setColarAberto] = useState(false);

  function alterarLista(chave: string, alterada: ListaForm) {
    onChange(listas.map((lista) => (lista.chave === chave ? alterada : lista)));
  }

  function removerLista(chave: string) {
    const lista = listas.find((item) => item.chave === chave);
    const temConteudo =
      lista &&
      (lista.fornecedor.trim() ||
        lista.itens.some((item) => item.descricao.trim()));
    if (temConteudo && !window.confirm("Remover esta lista de fornecedor?"))
      return;
    onChange(listas.filter((item) => item.chave !== chave));
  }

  return (
    <section className="mt-6">
      <div className="mb-3">
        <h2 className="text-base font-semibold text-neutral-900">
          Listas de fornecedores
        </h2>
        <p className="text-sm text-neutral-600">
          Pedidos que chegam em Birigui e vão junto com a produção para
          Araçatuba.
        </p>
      </div>

      {listas.length === 0 ? (
        <p className="rounded-xl border border-dashed border-neutral-300 px-3 py-6 text-center text-sm text-neutral-500">
          {somenteLeitura
            ? "Nenhuma lista de fornecedor nesta ficha."
            : "Nenhuma lista ainda. Use Adicionar lista ou cole a mensagem do WhatsApp."}
        </p>
      ) : (
        <div className="space-y-3">
          {listas.map((lista, indice) =>
            somenteLeitura ? (
              <BlocoLeitura
                key={lista.chave}
                lista={lista}
                previsaoEntrega={previsaoEntrega}
              />
            ) : (
              <BlocoEdicao
                key={lista.chave}
                lista={lista}
                numero={indice + 1}
                dataFicha={dataFicha}
                previsaoEntrega={previsaoEntrega}
                onChange={(alterada) => alterarLista(lista.chave, alterada)}
                onRemover={() => removerLista(lista.chave)}
              />
            )
          )}
        </div>
      )}

      {somenteLeitura ? null : (
        <div className="mt-3 flex flex-col gap-2 sm:flex-row">
          <button
            type="button"
            onClick={() => onChange([...listas, novaListaForm()])}
            className="inline-flex items-center justify-center gap-1.5 rounded-md border border-primary/30 bg-primary/5 px-4 py-2.5 text-sm font-semibold text-primary"
          >
            <Plus className="h-4 w-4" />
            Adicionar lista
          </button>
          <button
            type="button"
            onClick={() => setColarAberto(true)}
            className="inline-flex items-center justify-center gap-1.5 rounded-md border border-emerald-300 bg-emerald-50 px-4 py-2.5 text-sm font-semibold text-emerald-800"
          >
            <ClipboardPaste className="h-4 w-4" />
            Colar do WhatsApp
          </button>
        </div>
      )}

      <datalist id={ID_DIAS_CHEGADA}>
        {DIAS_SEMANA_BASE.map((dia) => (
          <option key={dia.valor} value={dia.label} />
        ))}
      </datalist>

      <ColarWhatsappModal
        isOpen={colarAberto}
        onClose={() => setColarAberto(false)}
        onAdicionar={(novas) => {
          onChange([...listas, ...novas.map((lista) => novaListaForm(lista))]);
          setColarAberto(false);
          onImportadas?.(novas.length);
        }}
      />
    </section>
  );
}

function BlocoEdicao({
  lista,
  numero,
  dataFicha,
  previsaoEntrega,
  onChange,
  onRemover,
}: {
  lista: ListaForm;
  numero: number;
  dataFicha: string;
  previsaoEntrega: string;
  onChange: (lista: ListaForm) => void;
  onRemover: () => void;
}) {
  function alterarItem(
    chave: string,
    campo: keyof Omit<ItemListaForm, "chave">,
    valor: string
  ) {
    onChange({
      ...lista,
      itens: lista.itens.map((item) =>
        item.chave === chave ? { ...item, [campo]: valor } : item
      ),
    });
  }

  return (
    <article className="rounded-2xl border border-neutral-200 bg-white p-3 shadow-sm sm:p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary">
          <Truck className="h-3.5 w-3.5" />
          Lista {numero}
        </span>
        <button
          type="button"
          onClick={onRemover}
          className="inline-flex items-center gap-1 text-sm font-semibold text-red-700"
        >
          <Trash2 className="h-4 w-4" />
          Remover lista
        </button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <label className="text-sm font-medium text-neutral-700">
          Fornecedor
          <input
            value={lista.fornecedor}
            onChange={(event) =>
              onChange({ ...lista, fornecedor: event.target.value })
            }
            placeholder="Ex.: Mira, Cortez, Devito"
            maxLength={120}
            className={`mt-1 ${INPUT}`}
          />
        </label>
        <label className="text-sm font-medium text-neutral-700">
          Previsão de chegada
          <input
            value={lista.previsaoChegada}
            onChange={(event) =>
              onChange({ ...lista, previsaoChegada: event.target.value })
            }
            list={ID_DIAS_CHEGADA}
            placeholder="Ex.: Quarta-feira"
            maxLength={60}
            className={`mt-1 ${INPUT}`}
          />
        </label>
        <div className="text-sm font-medium text-neutral-700 sm:col-span-2 lg:col-span-1">
          <label htmlFor={`data-transferencia-${lista.chave}`}>
            Data de transferência
          </label>
          <input
            id={`data-transferencia-${lista.chave}`}
            type="date"
            value={lista.dataTransferencia}
            min={dataFicha}
            onChange={(event) =>
              onChange({ ...lista, dataTransferencia: event.target.value })
            }
            className={`mt-1 ${INPUT} ${
              lista.dataTransferencia ? "border-primary/50 bg-primary/5" : ""
            }`}
          />
          <p className="mt-1 flex flex-wrap items-center gap-x-2 text-xs font-normal text-neutral-500">
            <span>
              {lista.dataTransferencia
                ? `Transferência: ${rotuloTransferenciaLista(lista.dataTransferencia, previsaoEntrega)}`
                : `Vazio: vai com a ficha${previsaoEntrega ? ` (${previsaoEntrega})` : ""}.`}
            </span>
            {lista.dataTransferencia ? (
              <button
                type="button"
                onClick={() => onChange({ ...lista, dataTransferencia: "" })}
                className="font-semibold text-primary"
              >
                Usar a da ficha
              </button>
            ) : null}
          </p>
        </div>
      </div>

      <div className="mt-4">
        <div className="mb-1 hidden grid-cols-[5rem_9rem_1fr_2.5rem] gap-2 px-1 text-[11px] font-semibold uppercase tracking-wide text-neutral-500 sm:grid">
          <span>Qtd</span>
          <span>Unidade</span>
          <span>Produto</span>
          <span />
        </div>
        <div className="space-y-2">
          {lista.itens.map((item) => (
            <div
              key={item.chave}
              className="grid grid-cols-[4.5rem_1fr_2.5rem] gap-2 rounded-xl bg-neutral-50 p-2 sm:grid-cols-[5rem_9rem_1fr_2.5rem] sm:bg-transparent sm:p-0"
            >
              <input
                value={item.quantidade}
                onChange={(event) =>
                  alterarItem(item.chave, "quantidade", event.target.value)
                }
                inputMode="decimal"
                placeholder="Qtd"
                aria-label="Quantidade"
                maxLength={30}
                className={`${INPUT} px-2 text-right tabular-nums`}
              />
              <input
                value={item.unidade}
                onChange={(event) =>
                  alterarItem(item.chave, "unidade", event.target.value)
                }
                placeholder="un, kg, cx…"
                aria-label="Unidade"
                maxLength={40}
                className={INPUT}
              />
              <input
                value={item.descricao}
                onChange={(event) =>
                  alterarItem(item.chave, "descricao", event.target.value)
                }
                placeholder="Produto"
                aria-label="Produto"
                maxLength={200}
                className={`${INPUT} order-4 col-span-3 sm:order-3 sm:col-span-1`}
              />
              <button
                type="button"
                onClick={() =>
                  onChange({
                    ...lista,
                    itens: lista.itens.filter(
                      (atual) => atual.chave !== item.chave
                    ),
                  })
                }
                aria-label="Remover linha"
                title="Remover linha"
                className="order-3 inline-flex h-10 w-10 items-center justify-center rounded-lg text-neutral-500 hover:bg-red-50 hover:text-red-700 sm:order-4"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={() =>
            onChange({ ...lista, itens: [...lista.itens, novoItemLista()] })
          }
          className="mt-2 inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-sm font-semibold text-primary hover:bg-primary/5"
        >
          <Plus className="h-4 w-4" />
          Adicionar linha
        </button>
      </div>
    </article>
  );
}

function BlocoLeitura({
  lista,
  previsaoEntrega,
}: {
  lista: ListaForm;
  previsaoEntrega: string;
}) {
  const itens = lista.itens.filter((item) => item.descricao.trim());
  return (
    <article className="rounded-2xl border border-neutral-200 bg-white p-3 shadow-sm sm:p-4">
      <div className="flex flex-wrap items-center gap-2">
        <Truck className="h-4 w-4 text-primary" />
        <h3 className="text-base font-semibold text-neutral-900">
          {lista.fornecedor || "Fornecedor"}
        </h3>
        {lista.previsaoChegada ? (
          <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-900">
            Chegada: {lista.previsaoChegada}
          </span>
        ) : null}
        <span
          className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
            lista.dataTransferencia
              ? "bg-primary/10 text-primary"
              : "bg-neutral-100 text-neutral-700"
          }`}
        >
          Transferência:{" "}
          {rotuloTransferenciaLista(lista.dataTransferencia, previsaoEntrega)}
        </span>
      </div>
      {itens.length === 0 ? (
        <p className="mt-2 text-sm text-neutral-500">Nenhum item.</p>
      ) : (
        <ul className="mt-2 divide-y divide-neutral-100">
          {itens.map((item) => (
            <li
              key={item.chave}
              className="flex items-start justify-between gap-3 py-1.5 text-sm"
            >
              <span className="text-neutral-800">{item.descricao}</span>
              <span className="shrink-0 font-semibold tabular-nums text-neutral-900">
                {quantidadeListaFornecedor(item) || "—"}
              </span>
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}

function ColarWhatsappModal({
  isOpen,
  onClose,
  onAdicionar,
}: {
  isOpen: boolean;
  onClose: () => void;
  onAdicionar: (listas: ListaPedidoWhatsapp[]) => void;
}) {
  const [texto, setTexto] = useState("");
  const [selecionadas, setSelecionadas] = useState<number[] | null>(null);
  const pedido = useMemo(() => interpretarPedidoWhatsapp(texto), [texto]);
  const indices = selecionadas ?? secoesPadraoDestino(pedido.secoes);
  const listas = listasDoPedido(pedido, indices);
  const totalItens = listas.reduce(
    (soma, lista) => soma + lista.itens.length,
    0
  );

  function fechar() {
    setTexto("");
    setSelecionadas(null);
    onClose();
  }

  function alternarSecao(indice: number) {
    setSelecionadas(
      indices.includes(indice)
        ? indices.filter((atual) => atual !== indice)
        : [...indices, indice]
    );
  }

  async function colarDaAreaDeTransferencia() {
    try {
      const copiado = await navigator.clipboard.readText();
      if (copiado) {
        setTexto(copiado);
        setSelecionadas(null);
      }
    } catch {
      // Sem permissão de leitura: o usuário cola manualmente no campo.
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={fechar}
      title="Colar pedido do WhatsApp"
      description="Copie a mensagem do fornecedor no WhatsApp e cole aqui. O sistema monta a lista e você confere antes de salvar."
      panelClassName="max-w-2xl"
    >
      <div className="space-y-4">
        <div>
          <div className="mb-1 flex items-center justify-between gap-2">
            <span className="text-sm font-medium text-neutral-700">
              Mensagem
            </span>
            {typeof navigator !== "undefined" &&
            typeof navigator.clipboard?.readText === "function" ? (
              <button
                type="button"
                onClick={() => void colarDaAreaDeTransferencia()}
                className="inline-flex items-center gap-1 text-sm font-semibold text-primary"
              >
                <ClipboardPaste className="h-4 w-4" />
                Colar copiado
              </button>
            ) : null}
          </div>
          <textarea
            value={texto}
            onChange={(event) => {
              setTexto(event.target.value);
              setSelecionadas(null);
            }}
            rows={9}
            placeholder={
              "MIRA — pedido da semana\n\nARAÇATUBA — CP DA SILVA:\n• 8 un — Bisnaga leitinho 1 kg\n• 1 lata — Cereja 1,8 kg"
            }
            className="block w-full rounded-lg border border-neutral-300 px-3 py-2 font-mono text-sm"
          />
        </div>

        {texto.trim() && pedido.secoes.length === 0 ? (
          <p className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-950">
            Não encontrei itens. Cada item precisa estar numa linha, de
            preferência começando pela quantidade. Exemplo: 8 un — Bisnaga
            leitinho 1 kg.
          </p>
        ) : null}

        {pedido.secoes.length > 0 ? (
          <div className="space-y-3 rounded-xl border border-neutral-200 bg-neutral-50 p-3">
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
              <span>
                <span className="text-neutral-500">Fornecedor: </span>
                <strong className="text-neutral-900">
                  {pedido.fornecedor || "não identificado (preencha depois)"}
                </strong>
              </span>
              <span>
                <span className="text-neutral-500">Chegada: </span>
                <strong className="text-neutral-900">
                  {pedido.previsaoChegada || "não informada"}
                </strong>
              </span>
            </div>

            {pedido.observacoes.length > 0 ? (
              <p className="text-xs text-neutral-600">
                Observação do fornecedor: {pedido.observacoes.join(" ")}
              </p>
            ) : null}

            {pedido.secoes.length > 1 ? (
              <p className="text-sm font-medium text-neutral-800">
                A mensagem tem partes para mais de uma loja. Marque o que vai
                nesta transferência:
              </p>
            ) : null}

            <div className="space-y-2">
              {pedido.secoes.map((secao, indice) => {
                const marcada = indices.includes(indice);
                return (
                  <label
                    key={`${secao.titulo}-${indice}`}
                    className={`block cursor-pointer rounded-xl border bg-white p-3 ${
                      marcada
                        ? "border-primary ring-1 ring-primary/30"
                        : "border-neutral-200 opacity-70"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {pedido.secoes.length > 1 ? (
                        <input
                          type="checkbox"
                          checked={marcada}
                          onChange={() => alternarSecao(indice)}
                          className="h-4 w-4 accent-primary"
                        />
                      ) : null}
                      <span className="text-sm font-semibold text-neutral-900">
                        {secao.titulo || "Itens"}
                      </span>
                      <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-[11px] font-semibold text-neutral-700">
                        {secao.itens.length}{" "}
                        {secao.itens.length === 1 ? "item" : "itens"}
                      </span>
                    </div>
                    <ul className="mt-2 space-y-0.5 text-xs text-neutral-700">
                      {secao.itens.map((item, posicao) => (
                        <li
                          key={posicao}
                          className="flex justify-between gap-3"
                        >
                          <span>{item.descricao}</span>
                          <span className="shrink-0 font-semibold tabular-nums">
                            {quantidadeListaFornecedor(item) || "—"}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </label>
                );
              })}
            </div>
          </div>
        ) : null}

        <div className="flex flex-col-reverse gap-2 pt-1 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={fechar}
            className="rounded-md border border-neutral-300 px-4 py-2 text-sm font-semibold text-neutral-700"
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={listas.length === 0}
            onClick={() => {
              onAdicionar(listas);
              setTexto("");
              setSelecionadas(null);
            }}
            className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
          >
            {listas.length > 1
              ? `Adicionar ${listas.length} listas (${totalItens} itens)`
              : `Adicionar lista (${totalItens} itens)`}
          </button>
        </div>
      </div>
    </Modal>
  );
}
